import { gql } from 'urql'
import { ERASURE_EXCEPTIONS } from '../constants/privacyDuties'

/**
 * Módulo Privacidad (A-30, Ley 21.719).
 * Diseño: docs/seguridad/ley_21719_proteccion_de_datos/a30-panel-privacidad.md
 */

export const CASE_TYPE_LABELS = {
  ACCESS: 'Acceso',
  RECTIFICATION: 'Rectificación',
  ERASURE: 'Supresión',
  OBJECTION: 'Oposición',
  PORTABILITY: 'Portabilidad',
  BLOCK: 'Bloqueo',
}

/** Texto corto para el alta: el listado y los filtros siguen usando CASE_TYPE_LABELS. */
export const CASE_TYPE_HINTS = {
  ACCESS: 'qué datos tenemos del titular',
  RECTIFICATION: 'corregir un dato inexacto o incompleto',
  ERASURE: 'borrar o anonimizar',
  OBJECTION: 'dejar de tratar para una finalidad',
  PORTABILITY: 'entregar los datos para llevarlos a otro lado',
  BLOCK: 'congelar el tratamiento de forma temporal',
}

export function caseTypeSelectLabel(type) {
  const label = CASE_TYPE_LABELS[type] || type
  const hint = CASE_TYPE_HINTS[type]
  return hint ? `${label} (${hint})` : label
}

export const CASE_STATUS_LABELS = {
  RECEIVED: 'Recibida',
  VERIFYING: 'Verificando identidad',
  IN_ANALYSIS: 'En análisis',
  ESCALATED_TO_CONTROLLER: 'Escalado al responsable',
  RESOLVED: 'Resuelto',
  PARTIALLY_DENIED: 'Parcialmente denegado',
  DENIED: 'Denegado',
  CLOSED: 'Cerrado',
}

/**
 * Estado visible alineado al paso actual del flujo (riel), no solo al enum.
 * Ej.: en Eliminación → «En proceso de eliminación».
 */
export function caseStatusLabel(privacyCase) {
  if (!privacyCase) return '—'
  const { status } = privacyCase

  if (status === 'CLOSED') return CASE_STATUS_LABELS.CLOSED
  if (status === 'DENIED') return CASE_STATUS_LABELS.DENIED
  if (status === 'PARTIALLY_DENIED') return CASE_STATUS_LABELS.PARTIALLY_DENIED
  if (status === 'ESCALATED_TO_CONTROLLER') {
    return CASE_STATUS_LABELS.ESCALATED_TO_CONTROLLER
  }
  if (status === 'RESOLVED') return 'Resuelto · pendiente de cierre'

  const next = nextCaseAction(privacyCase)
  if (next?.id === 'ack') return 'Pendiente de acuse'
  if (next?.id === 'verify') return 'Verificando identidad'
  if (next?.id === 'erasure') return 'En proceso de eliminación'
  if (next?.id === 'plan') return 'En definición del plan'
  if (next?.id === 'respond') return 'En respuesta'
  if (next?.id === 'close') return 'Pendiente de cierre'
  if (next?.id === 'done') return CASE_STATUS_LABELS.CLOSED

  return CASE_STATUS_LABELS[status] || status
}

export const HOLD_STATUS_LABELS = {
  NONE: 'Sin decidir',
  ACTIVE: 'Activo',
  REJECTED: 'Rechazado',
  LIFTED: 'Levantado',
}

export const CONTROLLER_ROLE_LABELS = {
  RESPONSABLE: 'Responsable',
  ENCARGADO: 'Encargado',
}

export const CHANNEL_LABELS = {
  EMAIL_DPO: 'dpo@smartcfo.cl',
  FORM: 'Formulario',
  ADMIN_MANUAL: 'Alta manual',
}

export const SCOPE_LABELS = {
  COMPANY: 'Empresa',
  USER: 'Usuario',
  RECORD: 'Registro',
}

/** Texto corto para el alta: el detalle del caso sigue usando SCOPE_LABELS. */
export const SCOPE_HINTS = {
  COMPANY: 'todos los datos de la empresa',
  USER: 'solo esa cuenta',
  RECORD: 'un dato o registro concreto',
}

export function scopeSelectLabel(scope) {
  const label = SCOPE_LABELS[scope] || scope
  const hint = SCOPE_HINTS[scope]
  return hint ? `${label} (${hint})` : label
}

/**
 * Vocabulario de cada derecho: qué pide el titular, qué se va a hacer y qué le
 * toca al equipo. Sin esto una oposición y una portabilidad se tramitan con la
 * misma ficha y el expediente no dice qué se resolvió.
 */
export const REQUEST_COPY = {
  ACCESS: {
    right: 'Acceso · Art. 5',
    summary: 'Quiere saber qué datos suyos tenemos y recibir una copia.',
    whatLabel: 'Qué información pide',
    whatOptions: [
      'Todos sus datos personales',
      'Los datos de un período determinado',
      'De dónde salieron sus datos (origen)',
      'Con quién se compartieron (destinatarios)',
      'Para qué se están usando (finalidad)',
      'Copia de un documento o registro puntual',
    ],
    targetLabel: 'Sobre qué datos o período',
    targetHint: 'Ej.: cartolas de 2024, contrato de servicios, correos de cobranza.',
    planLabel: 'Qué se le va a entregar',
    planOptions: [
      'Copia de sus datos en PDF',
      'Planilla con los datos del período pedido',
      'Detalle de origen, finalidad y destinatarios',
      'Certificado de que no tenemos datos suyos',
      'No procede: los datos son de la empresa cliente, no del titular',
    ],
    team: [
      'Reunir los datos del titular dentro del alcance pedido, no más.',
      'Tapar los datos de otras personas antes de enviar.',
      'La prueba de la respuesta es el JSON con el contenido íntegro, no un link.',
    ],
    planInstructions: [
      'Identifica qué datos entran en el alcance pedido (período, módulo o registro).',
      'Reúne la información desde las colecciones de origen, no sólo desde vistas del portal.',
      'Antes de exportar, tapa o elimina los datos de otras personas que aparezcan en el mismo archivo.',
      'Elige el formato de entrega acordado con el titular (PDF, planilla, certificado de inexistencia).',
    ],
    respondInstructions: [
      'Redacta qué datos se entregaron, en qué formato y qué período o alcance cubren.',
      'Si algo quedó fuera del alcance, explícalo con fundamento.',
      'Adjunta el JSON de auditoría con el contenido íntegro enviado; no un link.',
    ],
    identityWhy: 'Vas a entregar datos personales: si la identidad falla, es una filtración.',
    grantLabel: 'Entregar y responder',
    respondHint: 'Adjunta la copia que se entregó y dile qué incluye y qué período cubre.',
  },
  RECTIFICATION: {
    right: 'Rectificación · Art. 6',
    summary: 'Dice que un dato suyo está mal y quiere que lo corrijamos.',
    whatLabel: 'Qué problema tiene el dato',
    whatOptions: [
      'Un dato está equivocado',
      'Un dato está incompleto',
      'Un dato está desactualizado',
      'Se le atribuye información que no es suya',
    ],
    targetLabel: 'Qué dato y cuál sería el valor correcto',
    targetHint: 'Ej.: el RUT figura 11.111.111-1 y el correcto es 12.345.678-5.',
    planLabel: 'Qué se va a corregir',
    planOptions: [
      'Corregir el dato en el portal',
      'Completar el dato que falta',
      'Actualizar el dato con la fuente vigente',
      'Corregir y avisar a quienes recibieron el dato',
      'No procede: el dato que consta es el correcto',
    ],
    team: [
      'Corregir en la colección de origen, no sólo en la vista del portal.',
      'Revisar si el dato viejo viajó a informes o integraciones ya emitidas.',
      'Avisar la corrección a los terceros a quienes se les comunicó (Art. 11).',
    ],
    planInstructions: [
      'Confirma cuál es el dato incorrecto y cuál es el valor correcto según el titular.',
      'Localiza la colección o integración donde vive el dato de origen.',
      'Verifica si el dato erróneo ya salió en informes, exportaciones o a terceros.',
      'Define si hay que notificar la corrección a destinatarios externos (Art. 11).',
    ],
    respondInstructions: [
      'Adjunta el JSON de confirmación: field, before, after, modifiedAt y modified: true.',
      'Indica qué dato se corrigió, el valor anterior y el valor nuevo.',
      'Menciona desde cuándo rige la corrección en el sistema.',
      'Si no se pudo corregir algo, funda por qué (dato correcto, fuera de alcance, etc.).',
    ],
    identityWhy: 'Vas a cambiar un dato del titular: hay que estar seguro de quién lo pide.',
    grantLabel: 'Corregir y responder',
    respondHint:
      'Confirma con el JSON que el dato quedó modificado y dile al titular qué valor quedó y desde cuándo.',
  },
  ERASURE: {
    right: 'Supresión · Art. 7',
    summary: 'Quiere que borremos sus datos.',
    whatLabel: 'Por qué pide que se borren',
    whatOptions: [
      'Retiró su consentimiento',
      'Los datos ya no son necesarios para la finalidad',
      'Terminó la relación contractual',
      'El tratamiento no tiene base legal',
      'Se opone al tratamiento y no hay motivo para seguir',
    ],
    targetLabel: 'Qué datos pide borrar',
    targetHint: 'Ej.: toda su cuenta, los documentos de 2023, un correo puntual.',
    planLabel: 'Qué se va a hacer con los datos',
    planOptions: [
      'Borrado físico de los registros',
      'Anonimizar: el dato deja de ser personal',
      'Borrar sólo el alcance pedido',
      'No procede: hay obligación legal de conservar',
    ],
    team: [
      'El detalle operativo va en el paso Eliminación (P1–P7) y termina en un acta.',
      'El borrado lo ejecuta Server con un job; el panel sólo deja la constancia.',
    ],
    planInstructions: [
      'En supresión no hay etapa Plan aparte: el plan es el wizard de Eliminación + el acta (manual §4.3).',
      'Define el modo de operación: borrado físico, anonimizar, mixto o no se borra (hold / identidad fallida).',
      'Anota el alcance: empresa completa, cuenta de usuario o registro puntual.',
      'Revisa excepciones Art. 7 antes de ejecutar: obligación legal, defensa de reclamación, etc.',
    ],
    respondInstructions: [
      'Detalla qué se borró físicamente y qué se anonimizó (campos o registros).',
      'Menciona qué quedó conservado por obligación legal o hold, con fundamento.',
      'Si hubo residuales (backups Atlas, logs Object Lock), indica hasta cuándo pueden persistir.',
      'La prueba de la carta es el JSON en el expediente (to, subject, body, sentAt); no un link.',
    ],
    identityWhy: 'El borrado no se deshace: sin identidad verificada no se ejecuta.',
    grantLabel: 'Cerrar la supresión y responder',
    respondHint:
      'Dile qué se borró, qué se anonimizó y qué quedó guardado por una obligación legal.',
  },
  OBJECTION: {
    right: 'Oposición · Art. 8',
    summary: 'Quiere que dejemos de tratar sus datos para una finalidad concreta.',
    whatLabel: 'A qué se opone',
    whatOptions: [
      'Al envío de comunicaciones comerciales',
      'A la elaboración de perfiles o decisiones automatizadas',
      'Al uso de sus datos para una finalidad distinta de la contratada',
      'A que sus datos se compartan con un tercero',
      'A todo tratamiento basado en interés legítimo',
    ],
    targetLabel: 'Qué tratamiento o finalidad concreta',
    targetHint: 'Ej.: correos de marketing del portal, scoring de cobranza, envío a un partner.',
    planLabel: 'Qué se va a dejar de hacer',
    planOptions: [
      'Excluirlo de las comunicaciones comerciales',
      'Desactivar el perfilamiento para su cuenta',
      'Dejar de usar sus datos para esa finalidad',
      'Dejar de compartir sus datos con ese tercero',
      'No procede: el tratamiento es necesario para el contrato o la ley',
    ],
    team: [
      'Se corta la finalidad objetada; la cuenta y el resto del servicio siguen.',
      'Dejar la marca de exclusión donde un bot o una carga masiva no la pise.',
      'Si el tratamiento sigue por ley o contrato, hay que fundarlo en la respuesta.',
    ],
    planInstructions: [
      'Identifica la finalidad o tratamiento concreto al que se opone el titular.',
      'Localiza dónde se activa ese tratamiento (flags, listas, integraciones, cron).',
      'Define la marca o exclusión que impide que bots o cargas masivas lo reactiven.',
      'Si algún tratamiento debe seguir por ley o contrato, prepara el fundamento.',
    ],
    respondInstructions: [
      'Indica qué tratamiento se detuvo y desde qué fecha.',
      'Si algún tratamiento continúa, explica la base legal o contractual.',
      'Confirma que la cuenta y el resto del servicio siguen operativos.',
    ],
    identityWhy: 'Vas a cortar un tratamiento de su cuenta: confirma que es él quien lo pide.',
    grantLabel: 'Dejar de tratar y responder',
    respondHint:
      'Dile qué tratamiento se detuvo y desde cuándo. Si alguno sigue, funda por qué.',
  },
  PORTABILITY: {
    right: 'Portabilidad · Art. 9',
    summary: 'Quiere llevarse sus datos a otro proveedor.',
    whatLabel: 'Qué quiere llevarse',
    whatOptions: [
      'Sus datos en un formato que pueda reutilizar',
      'Que los enviemos directamente a otro proveedor',
      'Los datos de un período determinado',
    ],
    targetLabel: 'Formato pedido o proveedor de destino',
    targetHint: 'Ej.: CSV al correo registrado, o traspaso a otro contador.',
    planLabel: 'Cómo se van a entregar',
    planOptions: [
      'Exportar en CSV',
      'Exportar en JSON',
      'Enviar los datos al proveedor indicado',
      'No procede: son datos elaborados por Smart CFO, no aportados por el titular',
    ],
    team: [
      'Van los datos que aportó el titular o que generó con su uso, no los inferidos.',
      'Formato estructurado y de uso común: nada de capturas ni PDF armado a mano.',
      'Si se envía a otro proveedor, verificar el destinatario antes de mandar.',
    ],
    planInstructions: [
      'Separa los datos aportados o generados por el titular de los inferidos o elaborados por Smart CFO.',
      'Elige un formato estructurado y reutilizable (CSV, JSON); evita capturas o PDFs armados a mano.',
      'Si el titular pidió envío directo a otro proveedor, verifica el destinatario antes de transmitir.',
    ],
    respondInstructions: [
      'Indica qué archivo o exportación se entregó y en qué formato.',
      'Menciona el canal de entrega (correo, portal, traspaso a tercero).',
      'Si algo no procedió (datos elaborados, fuera de alcance), funda la denegación parcial o total.',
    ],
    identityWhy: 'Los datos salen hacia afuera: confirma la identidad y el destinatario.',
    grantLabel: 'Entregar y responder',
    respondHint: 'Dile qué archivo se entregó, en qué formato y a quién se envió.',
  },
  BLOCK: {
    right: 'Bloqueo · Art. 8 ter',
    summary: 'Quiere que congelemos el tratamiento mientras se resuelve algo.',
    whatLabel: 'Qué pide congelar y por qué',
    whatOptions: [
      'Suspender el tratamiento mientras se resuelve otra solicitud',
      'Discute la exactitud de un dato',
      'Discute la licitud del tratamiento',
      'Necesita el dato para un reclamo y no quiere que se borre',
    ],
    targetLabel: 'Qué tratamiento o dato hay que congelar',
    targetHint: 'Ej.: la cobranza automática, el envío a un tercero, un documento.',
    planLabel: 'Qué se va a congelar',
    planOptions: [
      'Congelar el tratamiento del alcance indicado',
      'Conservar el dato pero dejar de usarlo',
      'No procede: hay obligación legal de seguir tratando',
    ],
    team: [
      'El bloqueo del Art. 11 se decide en 2 días hábiles: está arriba en la ficha.',
      'Congelar el uso sin borrar: el dato se conserva mientras dure la controversia.',
    ],
    planInstructions: [
      'Identifica qué tratamiento o dato debe congelarse sin borrarse.',
      'Decide en 2 días hábiles si procede el bloqueo temporal (Art. 8 ter).',
      'Marca en el sistema qué procesos quedan suspendidos mientras dura la controversia.',
    ],
    respondInstructions: [
      'Indica qué tratamiento quedó congelado y desde qué fecha.',
      'Explica hasta cuándo o hasta qué evento dura el bloqueo.',
      'Si se rechazó el bloqueo, funda por qué el tratamiento debe continuar.',
    ],
    identityWhy: 'Vas a congelar un tratamiento de su cuenta: confirma quién lo pide.',
    grantLabel: 'Confirmar el bloqueo y responder',
    respondHint: 'Dile qué quedó congelado, desde cuándo y hasta que se resuelva qué.',
  },
}

const DEFAULT_REQUEST_COPY = {
  right: 'Solicitud de titular',
  summary: 'Qué pide el titular.',
  whatLabel: 'Qué pide el titular',
  whatOptions: [],
  targetLabel: 'Sobre qué recae',
  targetHint: '',
  planLabel: 'Qué vamos a hacer',
  planOptions: [],
  team: [],
  planInstructions: [],
  respondInstructions: [],
  identityWhy: '',
  grantLabel: 'Conceder y responder',
  respondHint: '',
}

export function requestCopy(type) {
  return REQUEST_COPY[type] || DEFAULT_REQUEST_COPY
}

export const STEP_STATUS_LABELS = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En curso',
  DONE: 'Hecho',
  SKIPPED: 'Omitido',
  BLOCKED: 'Bloqueado',
}

export const RESIDUAL_TYPE_LABELS = {
  LEGAL_HOLD: 'Hold legal',
  ATLAS_BACKUP: 'Backup Atlas',
  AUDIT_LOGS_OBJECT_LOCK: 'Logs Object Lock',
  S3_LIFECYCLE_TAG: 'Tag S3 lifecycle',
  THIRD_PARTY: 'Tercero',
}

export const ACTA_STATUS_LABELS = {
  DRAFT: 'Borrador',
  ISSUED: 'Emitida',
  RESIDUALS_PENDING: 'Residuales pendientes',
  CLOSED: 'Cerrada',
}

export const ACTA_OPERATION_LABELS = {
  PHYSICAL: 'Borrado físico (se eliminan los registros)',
  ANONYMIZATION: 'Anonimización (el dato deja de ser personal)',
  MIXED: 'Mixto (parte se borra y parte se anonimiza)',
  NONE: 'Sin eliminación (hold o identidad no verificada)',
}

export const ACTA_OPERATION_OPTIONS = [
  { value: 'PHYSICAL', label: ACTA_OPERATION_LABELS.PHYSICAL },
  { value: 'ANONYMIZATION', label: ACTA_OPERATION_LABELS.ANONYMIZATION },
  { value: 'MIXED', label: ACTA_OPERATION_LABELS.MIXED },
  { value: 'NONE', label: ACTA_OPERATION_LABELS.NONE },
]

/** Comprobaciones del acta: ejecutado ≠ verificado. La prueba es un JSON que
 * se inserta en el acta (y en la bitácora WORM). No un link: los links caducan. */
export const ACTA_VERIFICATION_CHECKS = [
  {
    key: 'notInProduct',
    label: 'Ya no aparece en el producto',
    hint: 'Nadie puede ver ni exportar esos datos en el Portal SmartCFO.',
    jsonHint: 'Objeto JSON con las superficies revisadas y si apareció el dato.',
    jsonExample: `{
  "surfaces": ["ficha", "listado", "export"],
  "found": false
}`,
  },
  {
    key: 'notInDb',
    label: 'Ya no está en la base de datos',
    hint: 'Los conteos Mongo del alcance dan 0, o el residual está justificado.',
    jsonHint: 'Objeto JSON con los conteos por colección.',
    jsonExample: `{
  "counts": { "users": 0, "invoices": 0 },
  "scope": "companyId"
}`,
  },
  {
    key: 'notInFuzzySearch',
    label: 'Ya no sale en búsquedas internas',
    hint: 'Los índices *_fuzzy no siguen encontrando el dato.',
    jsonHint: 'Objeto JSON con el índice, la consulta y los hits.',
    jsonExample: `{
  "index": "*_fuzzy",
  "hits": 0
}`,
  },
  {
    key: 'noReingest',
    label: 'Los bots no volvieron a cargar los datos',
    hint: 'Tras el freeze, un ciclo de cron o colas no reescribió el alcance.',
    jsonHint: 'Objeto JSON con freeze, ciclo posterior y si reescribió.',
    jsonExample: `{
  "freezeApplied": true,
  "cronCycleAfter": true,
  "rewrote": false
}`,
  },
]

export function looksLikeConfirmationLink(text) {
  return /^(s3:\/\/|https?:\/\/)/i.test((text || '').trim())
}

/** Parsea el JSON que se inserta en el acta. No acepta un link. */
export function parseAuditJson(raw) {
  const text = (raw || '').trim()
  if (!text) return { ok: false, error: 'Complete el JSON de auditoría.' }
  if (looksLikeConfirmationLink(text)) {
    return { ok: false, error: 'No se pega un link: inserta el JSON con lo comprobado.' }
  }
  try {
    const parsed = JSON.parse(text)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'El JSON debe ser un objeto { ... }.' }
    }
    return { ok: true, value: parsed, text: JSON.stringify(parsed) }
  } catch {
    return { ok: false, error: 'JSON inválido.' }
  }
}

/** Claves de conteo del JSON de evidencia P7 (sin secretos). */
export const CREDENTIALS_EVIDENCE_COUNT_KEYS = [
  { key: 'passwords', label: 'passwords' },
  { key: 'api_keys', label: 'api_keys (revocadas → after 0 activas)' },
  { key: 'bankPasswordDetail', label: 'nominasnews.bankPasswordDetail' },
  { key: 'updateallsessions', label: 'updateallsessions' },
]

function toNonNegInt(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}

export function emptyCredentialsEvidenceForm() {
  const counts = {}
  for (const { key } of CREDENTIALS_EVIDENCE_COUNT_KEYS) {
    counts[key] = { before: 0, after: 0 }
  }
  return {
    counts,
    cutTest: { botAuthenticated: false, botWrote: false },
    note: '',
  }
}

/** Lee un JSON de evidencia P7 hacia el formulario estructurado. */
export function credentialsEvidenceFormFromJson(raw) {
  const form = emptyCredentialsEvidenceForm()
  const parsed = parseAuditJson(raw)
  if (!parsed.ok) return form
  const value = parsed.value
  const countsIn =
    value.counts && typeof value.counts === 'object' && !Array.isArray(value.counts)
      ? value.counts
      : {}
  for (const { key } of CREDENTIALS_EVIDENCE_COUNT_KEYS) {
    const row = countsIn[key]
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      form.counts[key] = {
        before: toNonNegInt(row.before),
        after: toNonNegInt(row.after),
      }
    } else if (typeof row === 'number') {
      form.counts[key] = { before: toNonNegInt(row), after: 0 }
    }
  }
  const cut =
    value.cutTest && typeof value.cutTest === 'object' && !Array.isArray(value.cutTest)
      ? value.cutTest
      : {}
  form.cutTest = {
    botAuthenticated: Boolean(cut.botAuthenticated),
    botWrote: Boolean(cut.botWrote),
  }
  form.note = typeof value.note === 'string' ? value.note : ''
  return form
}

/** Serializa el formulario P7 a JSON legible (evidencia del paso). */
export function credentialsEvidenceJsonFromForm(form) {
  const counts = {}
  for (const { key } of CREDENTIALS_EVIDENCE_COUNT_KEYS) {
    const row = form?.counts?.[key] || { before: 0, after: 0 }
    counts[key] = {
      before: toNonNegInt(row.before),
      after: toNonNegInt(row.after),
    }
  }
  const payload = {
    counts,
    cutTest: {
      botAuthenticated: Boolean(form?.cutTest?.botAuthenticated),
      botWrote: Boolean(form?.cutTest?.botWrote),
    },
  }
  if ((form?.note || '').trim()) payload.note = form.note.trim()
  return JSON.stringify(payload, null, 2)
}

/** Colecciones Mongo del playbook P5 (companyId o _id = companyId). */
export const OFFBOARDING_EVIDENCE_MONGO_KEYS = [
  { key: 'documents', label: 'documents' },
  { key: 'agendas', label: 'agendas' },
  { key: 'balances', label: 'balances' },
  { key: 'payments', label: 'payments' },
  { key: 'transactions', label: 'transactions' },
  { key: 'flows', label: 'flows' },
  { key: 'eerrs', label: 'eerrs' },
  { key: 'eoafs', label: 'eoafs' },
  { key: 'nominasnews', label: 'nominasnews' },
  { key: 'cashpositions', label: 'cashpositions (fuera de cascada)' },
  { key: 'bankbalances', label: 'bankbalances' },
  { key: 'billings', label: 'billings (_id = companyId)' },
  { key: 'credits', label: 'credits (_id = companyId)' },
  { key: 'nominas', label: 'nominas (_id = companyId)' },
]

export const OFFBOARDING_OPERATION_OPTIONS = [
  { value: 'PHYSICAL', label: 'Borrado físico' },
  { value: 'ANONYMIZATION', label: 'Anonimización' },
  { value: 'MIXED', label: 'Mixto' },
]

function emptyBeforeAfterMap(keys) {
  const map = {}
  for (const { key } of keys) {
    map[key] = { before: 0, after: 0 }
  }
  return map
}

function readBeforeAfterMap(source, keys) {
  const map = emptyBeforeAfterMap(keys)
  const src = source && typeof source === 'object' && !Array.isArray(source) ? source : {}
  for (const { key } of keys) {
    const row = src[key]
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      map[key] = {
        before: toNonNegInt(row.before),
        after: toNonNegInt(row.after),
      }
    } else if (typeof row === 'number') {
      map[key] = { before: toNonNegInt(row), after: 0 }
    }
  }
  return map
}

export function emptyOffboardingEvidenceForm(operationType = 'PHYSICAL') {
  return {
    operationType: operationType || 'PHYSICAL',
    mongo: emptyBeforeAfterMap(OFFBOARDING_EVIDENCE_MONGO_KEYS),
    s3: {
      clients: { before: 0, after: 0 },
      intake: { before: 0, after: 0 },
    },
    fuzzyHitsAfter: 0,
    note: '',
  }
}

/** Lee un JSON de evidencia P5 hacia el formulario estructurado. */
export function offboardingEvidenceFormFromJson(raw, fallbackOperationType) {
  const form = emptyOffboardingEvidenceForm(fallbackOperationType)
  const parsed = parseAuditJson(raw)
  if (!parsed.ok) return form
  const value = parsed.value
  const op = value.operationType
  if (op === 'PHYSICAL' || op === 'ANONYMIZATION' || op === 'MIXED') {
    form.operationType = op
  }
  form.mongo = readBeforeAfterMap(value.mongo, OFFBOARDING_EVIDENCE_MONGO_KEYS)
  const s3 =
    value.s3 && typeof value.s3 === 'object' && !Array.isArray(value.s3) ? value.s3 : {}
  form.s3 = {
    clients: {
      before: toNonNegInt(s3.clients?.before ?? s3.clientsObjectsBefore),
      after: toNonNegInt(s3.clients?.after ?? s3.clientsObjectsAfter),
    },
    intake: {
      before: toNonNegInt(s3.intake?.before ?? s3.intakeObjectsBefore),
      after: toNonNegInt(s3.intake?.after ?? s3.intakeObjectsAfter),
    },
  }
  form.fuzzyHitsAfter = toNonNegInt(value.fuzzyHitsAfter)
  form.note = typeof value.note === 'string' ? value.note : ''
  return form
}

/** Serializa el formulario P5 a JSON legible (evidencia del paso). */
export function offboardingEvidenceJsonFromForm(form) {
  const mongo = {}
  for (const { key } of OFFBOARDING_EVIDENCE_MONGO_KEYS) {
    const row = form?.mongo?.[key] || { before: 0, after: 0 }
    mongo[key] = {
      before: toNonNegInt(row.before),
      after: toNonNegInt(row.after),
    }
  }
  const payload = {
    operationType: form?.operationType || 'PHYSICAL',
    mongo,
    s3: {
      clients: {
        before: toNonNegInt(form?.s3?.clients?.before),
        after: toNonNegInt(form?.s3?.clients?.after),
      },
      intake: {
        before: toNonNegInt(form?.s3?.intake?.before),
        after: toNonNegInt(form?.s3?.intake?.after),
      },
    },
    fuzzyHitsAfter: toNonNegInt(form?.fuzzyHitsAfter),
  }
  if ((form?.note || '').trim()) payload.note = form.note.trim()
  return JSON.stringify(payload, null, 2)
}

export function buildResponseAuditJson({
  to,
  subject,
  body,
  outcome,
  deniedReason,
  claimNoticeGiven,
}) {
  const payload = {
    channel: 'email',
    to: (to || '').trim(),
    sentAt: new Date().toISOString(),
    body: (body || '').trim(),
  }
  if ((subject || '').trim()) payload.subject = subject.trim()
  if (outcome) payload.outcome = outcome
  if ((deniedReason || '').trim()) payload.deniedReason = deniedReason.trim()
  if (claimNoticeGiven) payload.claimNoticeGiven = true
  return JSON.stringify(payload)
}

/** Derechos que exigen JSON de confirmación de cambio al conceder. */
export const RECTIFICATION_CASE_TYPES = ['RECTIFICATION']

export function emptyRectificationEvidenceForm(privacyCase) {
  return {
    field: (privacyCase?.request?.target || '').trim(),
    before: '',
    after: '',
    collection: '',
    modifiedAt: new Date().toISOString(),
    note: '',
  }
}

export function rectificationEvidenceJsonFromForm(form) {
  const payload = {
    kind: 'RECTIFICATION_CONFIRMATION',
    modified: true,
    field: (form?.field || '').trim(),
    before: form?.before === undefined || form?.before === null ? '' : String(form.before),
    after: (form?.after || '').trim(),
    modifiedAt: (form?.modifiedAt || '').trim() || new Date().toISOString(),
  }
  if ((form?.collection || '').trim()) payload.collection = form.collection.trim()
  if ((form?.note || '').trim()) payload.note = form.note.trim()
  return JSON.stringify(payload, null, 2)
}

export function rectificationEvidenceFormFromJson(raw, privacyCase) {
  const base = emptyRectificationEvidenceForm(privacyCase)
  const text = (raw || '').trim()
  if (!text) return base
  try {
    const parsed = JSON.parse(text)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return base
    return {
      field: typeof parsed.field === 'string' ? parsed.field : base.field,
      before:
        parsed.before === undefined || parsed.before === null
          ? ''
          : String(parsed.before),
      after: typeof parsed.after === 'string' ? parsed.after : '',
      collection: typeof parsed.collection === 'string' ? parsed.collection : '',
      modifiedAt:
        typeof parsed.modifiedAt === 'string' && parsed.modifiedAt.trim()
          ? parsed.modifiedAt
          : base.modifiedAt,
      note: typeof parsed.note === 'string' ? parsed.note : '',
    }
  } catch {
    return base
  }
}

/** Valida en cliente el JSON de confirmación de rectificación (espejo del backend). */
export function parseRectificationEvidenceJsonClient(raw) {
  const text = (raw || '').trim()
  if (!text) return { ok: false, error: 'Falta el JSON de confirmación' }
  if (/^(s3:\/\/|https?:\/\/)/i.test(text)) {
    return { ok: false, error: 'No se pega un link: inserta el JSON' }
  }
  let parsed
  try {
    parsed = JSON.parse(text)
  } catch {
    return { ok: false, error: 'JSON inválido' }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Debe ser un objeto { ... }' }
  }
  if (parsed.modified !== true) {
    return { ok: false, error: 'modified debe ser true' }
  }
  if (!(typeof parsed.field === 'string' && parsed.field.trim())) {
    return { ok: false, error: 'Falta field (qué dato se corrigió)' }
  }
  if (!(typeof parsed.after === 'string' && parsed.after.trim())) {
    return { ok: false, error: 'Falta after (valor nuevo)' }
  }
  if (
    !(typeof parsed.modifiedAt === 'string' && parsed.modifiedAt.trim()) ||
    Number.isNaN(Date.parse(parsed.modifiedAt))
  ) {
    return { ok: false, error: 'modifiedAt debe ser una fecha ISO' }
  }
  return { ok: true, value: parsed, text: JSON.stringify(parsed) }
}

export function evidenceForCheck(items, key) {
  return (items || []).filter((item) => item.verificationKey === key)
}

export function actaDisplayName(acta, privacyCase) {
  if (!acta?.actaNumber) return ''
  const right = (CASE_TYPE_LABELS[privacyCase?.type] || 'privacidad').toLowerCase()
  const company = formatCompanyLabel(privacyCase)
  const base = `Acta de ${right} ${acta.actaNumber}`
  return company ? `${base} · ${company}` : base
}

/** JSON canónico del acta de eliminación para preview visual (indentado). */
export function formatActaAuditPreview(acta, privacyCase) {
  if (!acta) return ''
  const fromStored = (() => {
    const raw = (acta.auditJson || '').trim()
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  })()

  const checks = (acta.verification?.checks || []).map((item) => {
    let payload = null
    if (item.payloadJson) {
      try {
        payload = JSON.parse(item.payloadJson)
      } catch {
        payload = { raw: item.payloadJson }
      }
    }
    return {
      key: item.key,
      observation: item.observation || undefined,
      payload: payload || undefined,
      verifiedAt: item.verifiedAt || undefined,
      verifiedByEmail: item.verifiedByEmail || undefined,
    }
  })

  const openResiduals = (privacyCase?.residuals || acta.residuals || []).filter(
    (item) => !item.clearedAt,
  )

  const document = fromStored || {
    actaNumber: acta.actaNumber,
    status: acta.status,
    operationType: acta.operationType,
    environment: acta.environment,
    scope: privacyCase?.scope,
    totals: acta.totals || {},
    residuals: openResiduals.map((item) => ({
      type: item.type,
      note: item.note || undefined,
      expiresAt: item.expiresAt || undefined,
    })),
    verification: {
      notInProduct: Boolean(acta.verification?.notInProduct),
      notInDb: Boolean(acta.verification?.notInDb),
      notInFuzzySearch: Boolean(acta.verification?.notInFuzzySearch),
      noReingest: Boolean(acta.verification?.noReingest),
      checks,
    },
    signedBy: {
      dpoEmail: acta.signedBy?.dpoEmail,
      serverEmail: acta.signedBy?.serverEmail,
      signedAt: acta.signedBy?.signedAt,
    },
  }

  if (!fromStored && privacyCase?.scope) document.scope = privacyCase.scope
  if (!fromStored) {
    document.residuals = openResiduals.map((item) => ({
      type: item.type,
      note: item.note || undefined,
      expiresAt: item.expiresAt || undefined,
    }))
    if (checks.length) {
      document.verification = {
        ...(document.verification || {}),
        checks,
      }
    }
  }

  try {
    return JSON.stringify(document, null, 2)
  } catch {
    return acta.auditJson || ''
  }
}

/** Documento de cierre del expediente (respuesta + acta si hay) para preview. */
export function formatCloseCasePreview(privacyCase) {
  if (!privacyCase) return ''
  const resolution = privacyCase.resolution || {}
  let response = null
  if (resolution.responseJson) {
    try {
      response = JSON.parse(resolution.responseJson)
    } catch {
      response = { raw: resolution.responseJson }
    }
  }
  const document = {
    caseCode: privacyCase.caseCode,
    type: privacyCase.type,
    status: privacyCase.status,
    scope: privacyCase.scope || undefined,
    subject: {
      email: privacyCase.subject?.email,
      name: privacyCase.subject?.name,
      rut: privacyCase.subject?.rut,
    },
    companyId: privacyCase.companyId || undefined,
    companyName: privacyCase.companyName || undefined,
    request: privacyCase.request
      ? {
          detail: privacyCase.request.detail,
          target: privacyCase.request.target,
          plannedAction: privacyCase.request.plannedAction,
        }
      : undefined,
    resolution: {
      outcome: resolution.outcome,
      respondedAt: resolution.respondedAt,
      responseTo: resolution.responseTo,
      deniedReason: resolution.deniedReason || undefined,
      response,
      ...(resolution.rectificationJson
        ? (() => {
            try {
              return { rectification: JSON.parse(resolution.rectificationJson) }
            } catch {
              return { rectification: { raw: resolution.rectificationJson } }
            }
          })()
        : {}),
    },
  }
  if (privacyCase.erasureActa?.actaNumber) {
    try {
      document.erasureActa = privacyCase.erasureActa.auditJson
        ? JSON.parse(privacyCase.erasureActa.auditJson)
        : {
            actaNumber: privacyCase.erasureActa.actaNumber,
            status: privacyCase.erasureActa.status,
            operationType: privacyCase.erasureActa.operationType,
          }
    } catch {
      document.erasureActa = {
        actaNumber: privacyCase.erasureActa.actaNumber,
        status: privacyCase.erasureActa.status,
      }
    }
  }
  try {
    return JSON.stringify(document, null, 2)
  } catch {
    return ''
  }
}

export function formatCompanyLabel(privacyCase) {
  const id = (privacyCase?.companyId || '').trim()
  if (!id) return ''
  const alias = (privacyCase?.companyAlias || '').trim()
  return alias ? `${id} (${alias})` : id
}

export const EVIDENCE_KIND_LABELS = {
  S3_OBJECT: 'Objeto S3',
  EMAIL: 'Correo',
  TICKET: 'Ticket',
  PR: 'PR',
  BACKUP: 'Backup',
  SCREENSHOT: 'Captura',
  ATHENA_QUERY: 'Consulta Athena',
  CLOUDTRAIL: 'CloudTrail',
}

export const TERMINAL_STATUSES = [
  'RESOLVED',
  'PARTIALLY_DENIED',
  'DENIED',
  'CLOSED',
]

export const ERASURE_CASE_TYPES = ['ERASURE']

/** Derechos que congela el tratamiento sin borrar (Art. 8 ter). */
export const BLOCK_CASE_TYPES = ['BLOCK']

/** Paso sintético del playbook de bloqueo (no es checklist A-12). */
export const BLOCK_PLAYBOOK_STEP = 'BLOCK_FREEZE'

const BLOCK_ENV_HINT =
  'Staging primero. El bloqueo suspende el tratamiento; no ejecutes deleteMany ni surrogate de anonimización.'

/**
 * Metadatos públicos del playbook de bloqueo. Los cuerpos salen tras reveal (DPO).
 */
export function blockCasePlaybook() {
  return {
    stepCode: BLOCK_PLAYBOOK_STEP,
    where: 'Mongo (flags de tratamiento) + Rabbit/cron + producto',
    who: 'Equipo TI / Server ejecuta · DPO aprueba con contraseña',
    env: BLOCK_ENV_HINT,
    needsDpoPassword: true,
    approveHint: 'Contraseña DPO para revelar comandos',
    commandsLocked: true,
    commands: [],
  }
}

/** Derechos que entregan datos al titular o a un tercero (Art. 9). */
export const PORTABILITY_CASE_TYPES = ['PORTABILITY']

/** Paso sintético del playbook de portabilidad (no es checklist A-12). */
export const PORTABILITY_PLAYBOOK_STEP = 'PORTABILITY_EXPORT'

const PORTABILITY_ENV_HINT =
  'Staging primero si pruebas el script. Solo datos aportados o generados con el uso — no inferidos. CSV/JSON, no capturas.'

/**
 * Metadatos públicos del playbook de portabilidad. Los cuerpos salen tras reveal (DPO).
 */
export function portabilityCasePlaybook() {
  return {
    stepCode: PORTABILITY_PLAYBOOK_STEP,
    where: 'Mongo (lectura) → archivo CSV/JSON → canal de entrega',
    who: 'Equipo TI / Server exporta · DPO aprueba con contraseña',
    env: PORTABILITY_ENV_HINT,
    needsDpoPassword: true,
    approveHint: 'Contraseña DPO para revelar comandos',
    commandsLocked: true,
    commands: [],
  }
}

/** Texto del paso Identidad: se elige el método, no se pega el documento. */
export const IDENTITY_VERIFICATION_COPY = {
  title: 'Verificar identidad',
  intro: 'Elige cómo se comprobó. No pegues RUT, fotos ni el documento.',
  fieldLabel: 'Cómo se comprobó',
  options: [
    'Sesión autenticada (Portal SmartCFO)',
    'Correo y RUT coincidentes (con la cuenta)',
    'Cédula revisada por el DPO',
    'Mandato del representante legal',
  ],
  instructions: [
    'Elige cómo se comprobó: sesión del portal, correo+RUT coincidentes, cédula revisada por el DPO o mandato (manual §3).',
    'No pegues RUT, fotos, PDF de cédula ni copias del documento en el expediente.',
    'El mandato del representante legal se conserva fuera del panel.',
    'Sin identidad verificada no se entregan datos ni se ejecuta borrado.',
  ],
}

export const ACK_COPY = {
  title: 'Acusar recibo',
  intro: 'Siguiente paso registra que recibimos la solicitud.',
  instructions: [
    'Solo confirma que llegó: todavía no resuelve el fondo (manual §3).',
    'El plazo de 30 días corridos ya venía corriendo desde que entró el correo, no desde el acuse.',
    'Revisa derecho ejercido, titular y empresa antes de acusar.',
    'Si el plazo es ajustado, evalúa prórroga única (+30 corridos) antes de vencer.',
  ],
}

export const EXTEND_COPY = {
  fieldLabel: 'Prórroga',
  intro: 'Una vez, +30 días corridos (Art. 11).',
  noneLabel: 'No prorrogar',
  yesLabel: 'Prorrogar 30 días',
  reasonLabel: 'Motivo',
  options: [
    'Complejidad del alcance (no da el plazo)',
    'Coordinación con el cliente (somos encargados)',
    'Volumen de registros (revisión larga)',
  ],
  instructions: [
    'Sólo se puede prorrogar una vez, por 30 días corridos (Art. 11).',
    'Elige el motivo que mejor describe por qué no da el plazo original.',
    'La prórroga se registra junto con el acuse; no la dejes para después.',
  ],
}

export const CONSERVATION_HOLD_COPY = {
  fieldLabel: 'Motivo del hold de conservación',
  intro:
    'Si una norma o un litigio impide borrar, elige la causal del Art. 7 (excepciones a la supresión). Si no hay nada que conservar, no lo actives: omite el paso.',
  options: [...ERASURE_EXCEPTIONS],
  instructions: [
    'Las causales son las excepciones del Art. 7 de la Ley 21.719 (inciso segundo).',
    'Si aplica, activa el hold con la causal correcta antes de continuar con P5.',
    'Si no hay nada que conservar, omite el paso y sigue con la eliminación.',
    'Mientras el hold esté activo, los pasos destructivos quedan bloqueados.',
  ],
}

export const TEMPORAL_HOLD_REQUEST_COPY = {
  fieldLabel: 'Por qué el titular pide el bloqueo',
  options: [
    'El titular pidió suspender el tratamiento mientras se resuelve',
    'Hay controversia sobre la exactitud del dato',
    'Hay controversia sobre la licitud del tratamiento',
  ],
}

export const TEMPORAL_HOLD_DECIDE_COPY = {
  fieldLabel: 'Fundamento de la decisión',
  options: [
    'Se concede: hay controversia sobre exactitud o licitud',
    'Se concede: el titular lo pidió y no hay obligación de seguir tratando',
    'Se rechaza: no hay controversia pendiente',
    'Se rechaza: hay obligación legal de seguir tratando el dato',
  ],
  instructions: [
    'Decidir en 2 días hábiles (lun–vie, sin feriados; el sábado no cuenta).',
    'Congela el tratamiento sin borrar: el dato se conserva mientras dura la controversia.',
    'Si se rechaza, la decisión hay que comunicarla a la Agencia (Art. 11).',
    'Hold activo impide marcar DONE los pasos destructivos (P5, P2, P7).',
  ],
}

export const DENIAL_REASON_COPY = {
  fieldLabel: 'Fundamento (si deniegas o concedes en parte)',
  intro: 'Obligatorio para denegar o conceder en parte. Es la causal que va en la respuesta al titular.',
  options: [
    ...ERASURE_EXCEPTIONS,
    'Identidad del titular no verificada',
    'La solicitud es infundada o excesiva',
    'Los datos no están en este tratamiento',
  ],
  instructions: [
    'Redacta el contenido íntegro de la respuesta antes de conceder, conceder en parte o denegar.',
    'Para denegar o conceder en parte, el fundamento es obligatorio y va en la carta al titular.',
    'Conceder en parte y denegar exigen rol DPO, contraseña del portal y aviso de reclamo ante la Agencia.',
    'Guarda el JSON de auditoría con el texto enviado; no un link.',
  ],
}

/**
 * Instructivos por paso. Fuente operativa:
 * - smart_back/docs/seguridad/ley_21719_proteccion_de_datos/a30-manual-operativo-panel.md (§3–§7)
 * - smart_back/docs/seguridad/a12-procedimientos/ (P1–P7)
 */
export const ACTA_FLOW_COPY = {
  generate: [
    'Confirmar el modo de operación del acta (borrado físico, anonimización, mixto o ninguno).',
    'Al continuar se crea un borrador con número de acta.',
    'A continuación se completan las cuatro comprobaciones con JSON de evidencia, una por una.',
    'Ejecutado ≠ verificado: la prueba es el JSON insertado en el acta, no un enlace.',
  ],
  sign: [
    'Revisar el preview del acta (comprobaciones, totales, residuales y JSON completo).',
    'Firman DPO y Server: personas distintas (segregación de funciones).',
    'Al emitir se cierra el acta en el mismo paso. Los residuales documentados quedan en el acta y en la respuesta al titular; no impiden cerrar.',
  ],
  close: [
    'Solo para actas ya emitidas y aún no cerradas (casos anteriores).',
    'Los residuales declarados quedan en el acta; no hace falta limpiarlos para cerrar.',
  ],
}

const P5_OFFBOARDING_PREP = [
  'Quien ejecuta (Server/TI) ≠ quien aprueba el acta (DPO).',
  'P7 (credenciales) debe estar Hecho antes de tocar datos.',
  'Freeze Rabbit/cron del tenant antes de P5: mensajes ya en cola pueden reescribir sin volver a autenticarse. Sin freeze, la casilla noReingest del acta es falsa.',
  'Conteos Mongo antes y después con companyId como string y como ObjectId.',
]

/** Instructivos de P5 según tipo de operación (manual §7). */
export const P5_OFFBOARDING_INSTRUCTIONS = {
  PHYSICAL: [
    ...P5_OFFBOARDING_PREP,
    'Mongo: borrar cascada (documents, agendas, balances, billings, credits, nominas, payments, transactions, flows, eerrs, nominasnews, eoafs) + cashpositions y bankbalances a mano.',
    'S3 clientes + intake: borrar versiones con s3api delete-object --bypass-governance-retention (rol humano). No basta aws s3 rm.',
    'Si Object Lock / legal hold lo impide: residual y fecha de salida.',
    'Invalidar caché Redis/TTL del tenant o declararlo en el JSON del acta.',
  ],
  ANONYMIZATION: [
    ...P5_OFFBOARDING_PREP,
    'Mongo: surrogate irreversible (sin tabla de mapeo) + $unset de *_fuzzy.',
    'Revisar colecciones del alcance; companyId string y ObjectId.',
    'S3 clients/{companyId}/: eliminar o anonimizar con bypass GOVERNANCE en cada versión. Fuzzy con hits: 0.',
  ],
  MIXED: [
    ...P5_OFFBOARDING_PREP,
    'Documenta qué colección se borra y cuál se anonimiza.',
    'Borrado: cascada + S3 con --bypass-governance-retention. Anonimización: surrogate + limpiar *_fuzzy.',
    'cashpositions y colecciones fuera de cascada: contar y tratar a mano.',
  ],
  DEFAULT: [
    ...P5_OFFBOARDING_PREP,
    'El panel no ejecuta el borrado: lo corre Server/TI. Marca Hecho solo cuando esté hecho.',
    'Mongo + S3 clients/ + intake: deletes S3 con --bypass-governance-retention. Si anonimiza: limpiar *_fuzzy.',
  ],
}

const ERASURE_ENV_HINT =
  'Staging primero. Prod: bucket secure-smartcfo y events/production/. Staging: secure-smartcfo-staging y events/staging/.'

/**
 * Metadatos públicos del playbook (dónde/quién).
 * Los cuerpos de comandos NO van aquí: solo salen tras revealPrivacyErasurePlaybook (DPO + clave).
 */
export function procedureStepPlaybook(step, privacyCase, operationTypeOverride) {
  const code = step?.code
  const scope = privacyCase?.scope || 'COMPANY'
  const userId = (privacyCase?.userId || '').trim()

  if (code === 'P7_CREDENTIALS') {
    return {
      where: 'Mongo (credenciales) + RabbitMQ',
      who: 'Equipo TI / Server ejecuta · DPO aprueba con contraseña',
      env: ERASURE_ENV_HINT,
      needsDpoPassword: true,
      commandsLocked: true,
      commands: [],
    }
  }

  if (code === 'P5_OFFBOARDING') {
    const op =
      operationTypeOverride ||
      resolveErasureOperationType(privacyCase) ||
      'PHYSICAL'
    const mode =
      op === 'ANONYMIZATION'
        ? 'Anonimización'
        : op === 'PHYSICAL'
          ? 'Borrado físico'
          : op === 'MIXED'
            ? 'Mixto'
            : 'Borrado o anonimización'
    return {
      where: 'Mongo (datos financieros) + S3 clientes + S3 intake',
      who: `Equipo TI / Server ejecuta (${mode}) · DPO aprueba con contraseña`,
      env: ERASURE_ENV_HINT,
      needsDpoPassword: true,
      commandsLocked: true,
      commands: [],
      operationType: op,
    }
  }

  if (code === 'P2_AUDIT_LOGS') {
    return {
      where: 'S3 smartcfo-security-audit (Object Lock GOVERNANCE)',
      who: 'Humano TI con BypassGovernanceRetention · DPO aprueba con contraseña',
      env: ERASURE_ENV_HINT,
      needsDpoPassword: true,
      commandsLocked: true,
      commands: [],
    }
  }

  if (code === 'P4_BACKUPS') {
    return {
      where: 'MongoDB Atlas (snapshots) — no se edita',
      who: 'Equipo TI declara residual · DPO revisa fechas',
      env: 'fechaSalida ≈ eliminación + retención del clúster + margen',
      needsDpoPassword: false,
      commandsLocked: false,
      commands: [],
    }
  }

  if (code === 'P1_INTAKE') {
    return {
      where: 'Expediente (alcance)',
      who: 'DPO',
      env: `Alcance actual: ${scope}${userId ? ` · userId ${userId}` : ''}`,
      needsDpoPassword: false,
      commandsLocked: false,
      commands: [],
    }
  }

  if (code === 'P3_LEGAL_HOLD') {
    return {
      where: 'Expediente (hold de conservación)',
      who: 'DPO (contraseña al activar/levantar)',
      env: 'Mientras haya hold, no se borran esas categorías',
      needsDpoPassword: true,
      commandsLocked: false,
      commands: [],
    }
  }

  return null
}

/** Pasos de eliminación en lenguaje de operación. Los códigos P1–P7 siguen en el API. */
export const PROCEDURE_STEP_COPY = {
  P1_INTAKE: {
    label: 'Confirmar alcance',
    hint: 'Qué se va a borrar o anonimizar: empresa, usuario o un registro.',
    instructions: [
      'Elige Empresa, Usuario o Registro según lo que pidió el titular.',
      'Queda en el expediente y en el acta.',
      'Si hay norma o litigio, el hold se resuelve en el paso siguiente.',
    ],
  },
  P3_LEGAL_HOLD: {
    label: '¿Hay que conservar algo?',
    hint: 'Si aplica una excepción del Art. 7, activa el hold. Si no, omite.',
    instructions: CONSERVATION_HOLD_COPY.instructions,
  },
  P7_CREDENTIALS: {
    label: 'Cortar accesos y credenciales',
    hint: 'Mongo: passwords, api_keys, sesiones. Antes de borrar datos.',
    instructions: [
      'Inventariar en Mongo (sin volcar secretos): passwords, api_keys, nominasnews.bankPasswordDetail, updateallsessions.',
      'Borrar/revocar hasta conteo 0. api_keys: revocar todos los users de la empresa (si necesitan key después, regeneran).',
      'No tocar users.services en alcance COMPANY (cuenta multi-tenant).',
      'Freeze Rabbit/cron antes de P5. Probar corte: bot no autentica. Adjunta JSON; Siguiente pide contraseña DPO.',
    ],
    jsonHint:
      'Conteos antes/después y prueba de corte (bot). Sin secretos ni links. Utilizar el formulario o adjuntar un .json.',
    jsonExample: `{
  "counts": {
    "passwords": { "before": 0, "after": 0 },
    "api_keys": { "before": 0, "after": 0 },
    "bankPasswordDetail": { "before": 0, "after": 0 },
    "updateallsessions": { "before": 0, "after": 0 }
  },
  "cutTest": {
    "botAuthenticated": false,
    "botWrote": false
  }
}`,
  },
  P5_OFFBOARDING: {
    label: 'Borrar o anonimizar los datos',
    hint: 'Mongo (financiero) + S3 clientes + intake. El panel no ejecuta el borrado.',
    instructions: P5_OFFBOARDING_INSTRUCTIONS.DEFAULT,
    jsonHint:
      'Conteos Mongo/S3 antes→después, hits fuzzy y modo. S3: borrar con bypass GOVERNANCE. Borrado: after 0. Anonimización: after = residuales no personales. Sin links.',
    jsonExample: `{
  "operationType": "PHYSICAL",
  "mongo": {
    "documents": { "before": 0, "after": 0 },
    "agendas": { "before": 0, "after": 0 },
    "balances": { "before": 0, "after": 0 },
    "payments": { "before": 0, "after": 0 },
    "transactions": { "before": 0, "after": 0 },
    "flows": { "before": 0, "after": 0 },
    "eerrs": { "before": 0, "after": 0 },
    "eoafs": { "before": 0, "after": 0 },
    "nominasnews": { "before": 0, "after": 0 },
    "cashpositions": { "before": 0, "after": 0 },
    "bankbalances": { "before": 0, "after": 0 },
    "billings": { "before": 0, "after": 0 },
    "credits": { "before": 0, "after": 0 },
    "nominas": { "before": 0, "after": 0 }
  },
  "s3": {
    "clients": { "before": 0, "after": 0 },
    "intake": { "before": 0, "after": 0 }
  },
  "fuzzyHitsAfter": 0
}`,
  },
  P2_AUDIT_LOGS: {
    label: 'Limpiar logs de auditoría',
    hint: 'S3 WORM smartcfo-security-audit. Solo humano TI.',
    instructions: [
      'Prefijo events/{env}/company/{companyId}/. Rol del portal: nunca.',
      'Borrar versiones con --bypass-governance-retention.',
      'Si Object Lock lo impide: residual AUDIT_LOGS_OBJECT_LOCK.',
      'No borrar el resumen WORM del caso de privacidad (solo subjectHash).',
    ],
  },
  P4_BACKUPS: {
    label: 'Declarar backups',
    hint: 'Atlas no se edita. Se declara residual con fecha de salida.',
    instructions: [
      'Registrar residual ATLAS_BACKUP con fechaSalida.',
      'Tras un restore de contingencia: volver a ejecutar P7 + P5.',
      'Cierra el residual desde la lista cuando expire.',
    ],
  },
  P6_ACTA: {
    label: 'Emitir el acta',
    hint: 'La prueba es el JSON de cada casilla. Firman DPO y Server, personas distintas.',
    instructions: ACTA_FLOW_COPY.generate,
  },
  P1_VERIFY_CLOSE: {
    label: 'Comprobar y cerrar la ejecución',
    hint: 'Producto, base, búsquedas y un ciclo de bot sin reescribir.',
    instructions: [
      'Producto: ficha, listado y export — nadie ve ni exporta el dato.',
      'Base: conteos = 0 o residuales justificados.',
      'Fuzzy: *_fuzzy con hits: 0.',
      'Un ciclo de bot tras freeze: no re-ingesta.',
    ],
  },
}

/** Tipo de operación de eliminación según plan o acta (físico / anonimización / mixto). */
export function resolveErasureOperationType(privacyCase) {
  const actaType = privacyCase?.erasureActa?.operationType
  if (actaType && actaType !== 'NONE') return actaType

  const plan = (privacyCase?.request?.plannedAction || '').toLowerCase()
  if (/anonimiz/.test(plan)) {
    if (/borr|físic|fisic|mixt|elim/.test(plan)) return 'MIXED'
    return 'ANONYMIZATION'
  }
  if (/borrado físico|borrar|eliminar|suprim/.test(plan)) return 'PHYSICAL'
  if (/mixt/.test(plan)) return 'MIXED'
  return null
}

/** Modo del acta: acta existente → plan → evidencia P5 → MIXED por defecto. */
export function resolveActaOperationTypeFromCase(privacyCase) {
  const fromActa = privacyCase?.erasureActa?.operationType
  if (fromActa && fromActa !== 'NONE') return fromActa
  const fromPlan = resolveErasureOperationType(privacyCase)
  if (fromPlan) return fromPlan
  const p5 = (privacyCase?.procedure?.steps || []).find((s) => s.code === 'P5_OFFBOARDING')
  const raw = (p5?.payloadJson || '').trim()
  if (raw) {
    const parsed = parseAuditJson(raw)
    if (parsed.ok && parsed.value.operationType) {
      const op = parsed.value.operationType
      if (op === 'PHYSICAL' || op === 'ANONYMIZATION' || op === 'MIXED' || op === 'NONE') {
        return op
      }
    }
  }
  return 'MIXED'
}

export function procedureStepLabel(step) {
  return PROCEDURE_STEP_COPY[step?.code]?.label || step?.label || step?.code
}

export function procedureStepHint(step, privacyCase, operationTypeOverride) {
  const hint = PROCEDURE_STEP_COPY[step?.code]?.hint || ''
  if (step?.code === 'P5_OFFBOARDING') {
    const op = operationTypeOverride || resolveErasureOperationType(privacyCase)
    if (op === 'ANONYMIZATION') {
      return 'Operación: anonimización. El dato deja de ser personal; no se borra el registro entero.'
    }
    if (op === 'PHYSICAL') {
      return 'Operación: borrado físico. Se eliminan los registros del alcance.'
    }
    if (op === 'MIXED') {
      return 'Operación: mixta. Parte se borra y parte se anonimiza según el plan.'
    }
  }
  return hint
}

export function procedureStepInstructions(step, privacyCase, operationTypeOverride) {
  if (step?.code === 'P5_OFFBOARDING') {
    const op =
      operationTypeOverride || resolveErasureOperationType(privacyCase)
    if (op && P5_OFFBOARDING_INSTRUCTIONS[op]) return P5_OFFBOARDING_INSTRUCTIONS[op]
    return P5_OFFBOARDING_INSTRUCTIONS.DEFAULT
  }
  return PROCEDURE_STEP_COPY[step?.code]?.instructions || []
}

/** Instructivo del paso principal del flujo (acuse, identidad, plan, respuesta, bloqueo). */
export function caseStageInstructions(stageId, privacyCase) {
  if (!stageId || !privacyCase) return []
  const copy = requestCopy(privacyCase.type)

  if (stageId === 'ack') return ACK_COPY.instructions || []
  if (stageId === 'verify') return IDENTITY_VERIFICATION_COPY.instructions || []
  if (stageId === 'plan') return copy.planInstructions || []
  if (stageId === 'respond') return copy.respondInstructions || []
  if (stageId === 'hold') return TEMPORAL_HOLD_DECIDE_COPY.instructions || []
  return []
}

/** Instructivo del sub-flujo del acta según la pantalla visible. */
export function actaFlowInstructions(wizardKind, check) {
  if (wizardKind === 'acta-generate') return ACTA_FLOW_COPY.generate
  if (wizardKind === 'acta-sign') return ACTA_FLOW_COPY.sign
  if (wizardKind === 'acta-close') return ACTA_FLOW_COPY.close
  if (wizardKind === 'acta-check' && check) {
    return [
      `${check.label}: ${check.hint}`,
      'Completar el JSON de lo comprobado; se incorpora al acta y a la bitácora (10 años).',
      'No pegar un enlace: los enlaces caducan y no sirven como prueba.',
    ]
  }
  return []
}

/** Pasos A-12 que mutan datos o credenciales. Completarlos pide contraseña del DPO. */
export const DESTRUCTIVE_STEP_CODES = ['P5_OFFBOARDING', 'P2_AUDIT_LOGS', 'P7_CREDENTIALS']

const SENSITIVE_PRIVACY_ACTIONS = ['deny', 'issueActa', 'closeActa', 'setConservationHold']

/** Acciones que el backend exige rol DPO + confirmPassword (bcrypt del portal). */
export function privacyActionNeedsPassword(action, variables) {
  if (SENSITIVE_PRIVACY_ACTIONS.includes(action)) return true
  if (action === 'revealPlaybook') return true
  if (action === 'resolve' && variables?.input?.partial) return true
  if (action === 'updateStep') {
    const code = variables?.input?.code
    const status = variables?.input?.status
    return (
      DESTRUCTIVE_STEP_CODES.includes(code) &&
      (status === 'DONE' || status === 'SKIPPED')
    )
  }
  return false
}

/**
 * Título y texto del modal de contraseña DPO, según la acción.
 * Explica por qué el paso es sensible y pide la clave para continuar.
 */
export function dpoPasswordPromptCopy(action, variables) {
  const code = variables?.input?.code
  const status = variables?.input?.status
  const skipping = status === 'SKIPPED'
  const stepCode = variables?.input?.stepCode

  if (action === 'revealPlaybook') {
    return {
      title: 'Revelar comandos del paso',
      body:
        stepCode === 'P7_CREDENTIALS'
          ? 'Los comandos de corte de credenciales son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.'
          : stepCode === 'P5_OFFBOARDING'
            ? 'Los comandos de borrado/anonimización son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.'
            : stepCode === 'P2_AUDIT_LOGS'
              ? 'Los comandos de limpieza de logs WORM son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.'
              : stepCode === 'BLOCK_FREEZE'
                ? 'Los comandos de congelamiento (Art. 8 ter) son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.'
                : stepCode === 'PORTABILITY_EXPORT'
                  ? 'Los comandos de extracción para portabilidad (Art. 9) son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.'
                  : 'Los comandos de este paso son sensibles. Solo el DPO puede verlos. Escribe tu contraseña del portal.',
    }
  }

  if (action === 'updateStep' && code === 'P7_CREDENTIALS') {
    return {
      title: skipping ? 'Omitir corte de credenciales' : 'Confirmar revocación de credenciales',
      body: skipping
        ? 'Omitir el corte de accesos y credenciales es un paso sensible. Escribe tu contraseña del portal para continuar.'
        : 'Confirmar la revocación de credenciales es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'updateStep' && code === 'P5_OFFBOARDING') {
    return {
      title: skipping ? 'Omitir borrado de datos' : 'Confirmar borrado de datos',
      body: skipping
        ? 'Omitir el borrado o la anonimización es un paso sensible. Escribe tu contraseña del portal para continuar.'
        : 'Confirmar el borrado o la anonimización de datos es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'updateStep' && code === 'P2_AUDIT_LOGS') {
    return {
      title: skipping ? 'Omitir limpieza de logs' : 'Confirmar limpieza de logs',
      body: skipping
        ? 'Omitir la limpieza de logs de auditoría es un paso sensible. Escribe tu contraseña del portal para continuar.'
        : 'Confirmar la limpieza de logs de auditoría es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'deny') {
    return {
      title: 'Confirmar denegación',
      body: 'Denegar el derecho es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'resolve' && variables?.input?.partial) {
    return {
      title: 'Confirmar concesión parcial',
      body: 'Conceder en parte es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'setConservationHold') {
    return {
      title: 'Confirmar hold de conservación',
      body: 'Activar o levantar el hold de conservación es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'issueActa') {
    return {
      title: 'Confirmar emisión y cierre del acta',
      body: 'Emitir y cerrar el acta de eliminación es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  if (action === 'closeActa') {
    return {
      title: 'Confirmar cierre del acta',
      body: 'Cerrar el acta es un paso sensible. Escribe tu contraseña del portal para continuar.',
    }
  }
  return {
    title: 'Confirmar acción sensible',
    body: 'Esta acción es sensible. Escribe tu contraseña del portal para continuar.',
  }
}

export function erasureStepReached(index, privacyCase) {
  const steps = privacyCase?.procedure?.steps || []
  const step = steps[index]
  if (!step) return false
  const live = erasureWizardState(privacyCase)
  if (live.kind === 'done') return true
  if (index <= live.index) return true
  return ['DONE', 'SKIPPED', 'BLOCKED', 'IN_PROGRESS'].includes(step.status)
}

function stepOpen(step) {
  return step && ['PENDING', 'IN_PROGRESS', 'BLOCKED'].includes(step.status)
}

function actaInProgress(acta) {
  return Boolean(acta?.actaNumber) && ['DRAFT', 'ISSUED', 'RESIDUALS_PENDING'].includes(acta.status)
}

/**
 * Un paso visible a la vez en la eliminación/acta. El resto queda como riel.
 */
export function erasureWizardState(privacyCase) {
  const steps = privacyCase?.procedure?.steps || []
  const openIndex = steps.findIndex(stepOpen)
  const open = openIndex >= 0 ? steps[openIndex] : null
  const acta = privacyCase?.erasureActa
  const total = steps.length || 1
  const actaIndex = Math.max(
    0,
    steps.findIndex((step) => step.code === 'P6_ACTA'),
  )

  if (open && open.code !== 'P6_ACTA' && !actaInProgress(acta)) {
    return { kind: 'step', step: open, index: openIndex, total }
  }

  if (!acta?.actaNumber && (!open || open.code === 'P6_ACTA')) {
    return { kind: 'acta-generate', step: open, index: actaIndex, total }
  }

  if (acta?.status === 'DRAFT') {
    const checkIndex = ACTA_VERIFICATION_CHECKS.findIndex((check) => !acta.verification?.[check.key])
    if (checkIndex >= 0) {
      return {
        kind: 'acta-check',
        check: ACTA_VERIFICATION_CHECKS[checkIndex],
        checkIndex,
        checkTotal: ACTA_VERIFICATION_CHECKS.length,
        index: actaIndex,
        total,
      }
    }
    return { kind: 'acta-sign', index: actaIndex, total }
  }

  if (acta?.status === 'ISSUED' || acta?.status === 'RESIDUALS_PENDING') {
    return { kind: 'acta-close', index: actaIndex, total }
  }

  if (open) {
    return { kind: 'step', step: open, index: openIndex, total }
  }

  return { kind: 'done', index: total, total }
}

/** Qué tiene que hacer ahora quien opera el caso. */
export function nextCaseAction(privacyCase) {
  if (!privacyCase) return null
  if (privacyCase.status === 'CLOSED') {
    return { id: 'done', title: 'Expediente cerrado', hint: 'No hay más trámites en este caso.' }
  }
  if (!privacyCase.ackAt) {
    return {
      id: 'ack',
      title: ACK_COPY.title,
      hint: ACK_COPY.intro,
    }
  }
  if (!privacyCase.subject?.identityVerifiedAt) {
    return {
      id: 'verify',
      title: IDENTITY_VERIFICATION_COPY.title,
      hint: IDENTITY_VERIFICATION_COPY.intro,
    }
  }
  if (ERASURE_CASE_TYPES.includes(privacyCase.type)) {
    const open = (privacyCase.procedure?.steps || []).find(stepOpen)
    if (open) {
      return {
        id: 'erasure',
        title: procedureStepLabel(open),
        hint: procedureStepHint(open, privacyCase) || 'Marca el paso cuando Server o TI lo hayan hecho.',
      }
    }
  }
  if (caseNeedsPlan(privacyCase)) {
    const copy = requestCopy(privacyCase.type)
    return {
      id: 'plan',
      title: copy.planLabel,
      hint: 'Antes de responder hay que dejar por escrito qué se va a hacer con sus datos.',
    }
  }
  if (!privacyCase.resolution?.respondedAt && !TERMINAL_STATUSES.includes(privacyCase.status)) {
    const copy = requestCopy(privacyCase.type)
    return {
      id: 'respond',
      title: 'Responder al titular',
      hint: privacyCase.request?.plannedAction
        ? `Se resolvió: ${privacyCase.request.plannedAction}. Guarda qué se le envió.`
        : `${copy.summary} Concede, concede en parte o deniega, y guarda qué se le envió.`,
    }
  }
  if (privacyCase.status !== 'CLOSED') {
    return {
      id: 'close',
      title: 'Cerrar el expediente',
      hint: 'La respuesta ya salió. Cierra el expediente.',
    }
  }
  return { id: 'done', title: 'Listo', hint: '' }
}

/**
 * El plan se fija antes de responder. En **supresión** no aparece como etapa
 * aparte: lo cubre el checklist P1–P7 + acta. El **bloqueo** (Art. 8 ter) sí
 * tiene plan (qué congelar) y hold de 2 días hábiles; no usa el wizard de borrado.
 */
export function caseNeedsPlan(privacyCase) {
  if (!privacyCase) return false
  if (ERASURE_CASE_TYPES.includes(privacyCase.type)) return false
  if (TERMINAL_STATUSES.includes(privacyCase.status)) return false
  return !privacyCase.request?.plannedAction
}

export function caseFlowStages(privacyCase) {
  const erasure = ERASURE_CASE_TYPES.includes(privacyCase?.type)
  const block = privacyCase?.type === 'BLOCK'
  const stages = [
    { id: 'received', label: 'Recibida' },
    { id: 'ack', label: 'Acuse' },
    { id: 'verify', label: 'Identidad' },
  ]
  stages.push(
    erasure
      ? { id: 'erasure', label: 'Eliminación' }
      : { id: 'plan', label: block ? 'Bloqueo' : 'Plan' },
  )
  stages.push({ id: 'respond', label: 'Respuesta' }, { id: 'close', label: 'Cierre' })
  return stages
}

export function caseFlowStageStatus(stageId, privacyCase) {
  const next = nextCaseAction(privacyCase)
  const erasureDone =
    !ERASURE_CASE_TYPES.includes(privacyCase?.type) ||
    !(privacyCase?.procedure?.steps || []).some(stepOpen)

  const done = {
    received: true,
    ack: Boolean(privacyCase?.ackAt),
    verify: Boolean(privacyCase?.subject?.identityVerifiedAt),
    erasure: erasureDone,
    plan: Boolean(privacyCase?.request?.plannedAction) || !caseNeedsPlan(privacyCase),
    respond: Boolean(privacyCase?.resolution?.respondedAt) || TERMINAL_STATUSES.includes(privacyCase?.status),
    close: privacyCase?.status === 'CLOSED',
  }
  if (privacyCase?.status === 'CLOSED') return 'done'
  if (done[stageId]) return 'done'
  if (next?.id === stageId) return 'current'
  if (stageId === 'erasure' && next?.id === 'erasure') return 'current'
  return 'upcoming'
}

/** Resumen de un paso ya hecho, para consultarlo sin editar. */
export function stageReview(privacyCase, stageId) {
  if (!privacyCase || !stageId) return null
  if (caseFlowStageStatus(stageId, privacyCase) !== 'done') return null

  if (stageId === 'received') {
    const copy = requestCopy(privacyCase.type)
    return {
      title: 'Solicitud recibida',
      rows: [
        ['Titular', privacyCase.subject?.name || privacyCase.subject?.email || '—'],
        ['Correo', privacyCase.subject?.email || '—'],
        ['Derecho', CASE_TYPE_LABELS[privacyCase.type] || privacyCase.type],
        [copy.whatLabel, privacyCase.request?.detail || '—'],
        [copy.targetLabel, privacyCase.request?.target || '—'],
        ['Empresa', privacyCase.companyName || privacyCase.companyId || '—'],
        ['Canal', CHANNEL_LABELS[privacyCase.channel] || privacyCase.channel || '—'],
        ['Recibido', formatDateTime(privacyCase.receivedAt)],
      ],
    }
  }

  if (stageId === 'plan') {
    const copy = requestCopy(privacyCase.type)
    return {
      title: 'Qué se resolvió hacer',
      rows: [
        [copy.planLabel, privacyCase.request?.plannedAction || '—'],
        ['Definido por', privacyCase.request?.plannedByEmail || '—'],
        ['Cuándo', formatDateTime(privacyCase.request?.plannedAt)],
      ],
    }
  }

  if (stageId === 'ack') {
    const extended = Boolean(privacyCase.extension?.grantedAt)
    const rows = [
      ['Acusado', formatDateTime(privacyCase.ackAt)],
      ['Prórroga', extended ? 'Sí' : 'No'],
    ]
    if (extended) {
      rows.push(['Motivo', privacyCase.extension?.reason || '—'])
      rows.push(['Nuevo vencimiento', formatDate(privacyCase.extension?.dueAt)])
    }
    return { title: 'Acuse de recibo', rows }
  }

  if (stageId === 'verify') {
    return {
      title: 'Identidad',
      rows: [
        ['Comprobado', formatDateTime(privacyCase.subject?.identityVerifiedAt)],
        ['Método', privacyCase.subject?.identityMethod || '—'],
      ],
    }
  }

  if (stageId === 'erasure') {
    const rows = (privacyCase.procedure?.steps || []).map((step) => [
      procedureStepLabel(step),
      [
        STEP_STATUS_LABELS[step.status] || step.status,
        step.completedByEmail,
        step.completedAt ? formatDateTime(step.completedAt) : null,
      ]
        .filter(Boolean)
        .join(' · '),
    ])
    if (privacyCase.scope) {
      rows.unshift(['Alcance', SCOPE_LABELS[privacyCase.scope] || privacyCase.scope])
    }
    // El acta completa se renderiza aparte (ActaPreview). Aquí solo el checklist P1–P7.
    return { title: 'Eliminación', rows, showActa: Boolean(privacyCase.erasureActa?.actaNumber) }
  }

  if (stageId === 'respond') {
    const rows = [
      ['Respondido', formatDateTime(privacyCase.resolution?.respondedAt)],
      ['Destinatario', privacyCase.resolution?.responseTo || '—'],
    ]
    if (privacyCase.resolution?.responseJson) {
      rows.push(['Respuesta (JSON)', privacyCase.resolution.responseJson])
    }
    if (privacyCase.resolution?.deniedReason) {
      rows.push(['Fundamento', privacyCase.resolution.deniedReason])
    }
    return { title: 'Respuesta al titular', rows }
  }

  if (stageId === 'close') {
    return {
      title: 'Cierre',
      rows: [['Estado', caseStatusLabel(privacyCase)]],
    }
  }

  return null
}

/** Semáforo del vencimiento efectivo (el de la prórroga si se otorgó). */
export function dueSeverity(privacyCase) {
  if (!privacyCase) return 'neutral'
  if (TERMINAL_STATUSES.includes(privacyCase.status)) return 'neutral'
  if (privacyCase.isOverdue) return 'red'
  if (privacyCase.daysToDue <= 7) return 'amber'
  return 'green'
}

export function formatDate(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value))
}

export function formatDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

/** "vence en 5 días" / "vencido hace 2 días" */
export function formatDueLabel(privacyCase) {
  if (!privacyCase) return '—'
  const days = privacyCase.daysToDue
  if (privacyCase.isOverdue) {
    const late = Math.abs(days)
    return late === 0 ? 'vence hoy' : `vencido hace ${late} día${late === 1 ? '' : 's'}`
  }
  if (days === 0) return 'vence hoy'
  return `en ${days} día${days === 1 ? '' : 's'}`
}

const CASE_FIELDS = `
  id
  caseCode
  type
  controllerRole
  subject {
    rut
    email
    name
    identityVerifiedAt
    identityMethod
  }
  companyId
  companyName
  companyAlias
  userId
  scope
  channel
  request {
    detail
    target
    plannedAction
    plannedAt
    plannedByEmail
  }
  rawIntakeKey
  receivedAt
  ackAt
  dueAt
  effectiveDueAt
  daysToDue
  isOverdue
  extension {
    grantedAt
    dueAt
    reason
  }
  hold {
    requestedAt
    dueAt
    decidedAt
    status
    reason
    reportedToAgencyAt
  }
  conservationHold {
    active
    reason
    until
    setAt
    setByEmail
    clearedAt
    clearedByEmail
  }
  procedure {
    steps {
      code
      label
      status
      ownerRole
      note
      evidenceKeys
      payloadJson
      completedAt
      completedByEmail
    }
  }
  residuals {
    type
    expiresAt
    note
    clearedAt
    clearedByEmail
  }
  erasureActa {
    actaNumber
    status
    operationType
    totals {
      deleted
      anonymized
      s3Tagged
      s3Deleted
      credentialsRevoked
      collectionsProcessed
      collectionsInRegistry
    }
    residuals {
      type
      expiresAt
      note
      clearedAt
    }
    verification {
      notInProduct
      notInDb
      notInFuzzySearch
      noReingest
      verifiedAt
      verifiedByEmail
      note
      checks {
        key
        observation
        payloadJson
        verifiedAt
        verifiedByEmail
        auditEventId
      }
    }
    signedBy {
      dpoEmail
      serverEmail
      signedAt
    }
    executedAt
    issuedAt
    closedAt
    environment
    auditEventId
    auditJson
  }
  status
  resolution {
    outcome
    legalGrounds
    deniedReason
    executedActions {
      action
      scope
      jobId
      count
      executedAt
    }
    respondedAt
    responseTo
    responseJson
    rectificationJson
    responseBodyKey
    claimNoticeGiven
  }
  propagation {
    target
    notifiedAt
    note
  }
  escalation {
    contactEmails
    contactSource
    sentAt
    acknowledgedAt
  }
  evidence {
    id
    kind
    uri
    sha256
    description
    capturedAt
    retainUntil
    verificationKey
  }
  auditEventIds
  pendingAuditEvents
  createdAt
  updatedAt
`

export const GET_PRIVACY_DASHBOARD = gql`
  query PrivacyDashboard {
    privacyDashboard {
      open
      overdue
      dueWithin7Days
      awaitingAck
      activeHolds
      holdsUndecided
      holdsDueSoon
      escalatedToController
      auditOutboxPending
      erasuresOpen
      blocksOpen
      pendingClose
      missingCompany
      actasPending
      residualsOpen
    }
  }
`

export const GET_PRIVACY_CASES = gql`
  query PrivacyCases($filter: PrivacyCaseFilterInput) {
    privacyCases(filter: $filter) {
      ${CASE_FIELDS}
    }
  }
`

export const GET_PRIVACY_CASE = gql`
  query PrivacyCase($caseCode: String!) {
    privacyCase(caseCode: $caseCode) {
      ${CASE_FIELDS}
    }
  }
`

export const GET_CONTROLLER_CONTACT = gql`
  query PrivacyControllerContact($companyId: String!) {
    privacyControllerContact(companyId: $companyId)
  }
`

export const GET_PRIVACY_AUDIT_OUTBOX = gql`
  query PrivacyAuditOutbox {
    privacyAuditOutbox {
      pending
      sending
      sent
      failed
    }
  }
`

export const DISPATCH_PRIVACY_AUDIT_OUTBOX = gql`
  mutation DispatchPrivacyAuditOutbox {
    dispatchPrivacyAuditOutbox {
      claimed
      sent
      failed
      retried
      skippedReason
    }
  }
`

export const RUN_PRIVACY_EMAIL_INTAKE = gql`
  mutation RunPrivacyEmailIntake {
    runPrivacyEmailIntake {
      scanned
      created
      skipped
      errors
      mailbox
      createdCodes
      skipNotes
    }
  }
`

export const GET_PRIVACY_EMAIL_INTAKE = gql`
  query PrivacyEmailIntake {
    privacyEmailIntake {
      listening
      storedListening
      configured
      envForcedOff
      mailbox
    }
  }
`

export const SET_PRIVACY_EMAIL_INTAKE_LISTENING = gql`
  mutation SetPrivacyEmailIntakeListening($listening: Boolean!) {
    setPrivacyEmailIntakeListening(listening: $listening) {
      listening
      storedListening
      configured
      envForcedOff
      mailbox
    }
  }
`

export const CREATE_PRIVACY_CASE = gql`
  mutation CreatePrivacyCase($input: CreatePrivacyCaseInput!) {
    createPrivacyCase(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const ACKNOWLEDGE_PRIVACY_CASE = gql`
  mutation AcknowledgePrivacyCase($caseCode: String!) {
    acknowledgePrivacyCase(caseCode: $caseCode) {
      ${CASE_FIELDS}
    }
  }
`

export const UPDATE_PRIVACY_CASE_REQUEST = gql`
  mutation UpdatePrivacyCaseRequest($input: UpdatePrivacyCaseRequestInput!) {
    updatePrivacyCaseRequest(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const VERIFY_PRIVACY_CASE_IDENTITY = gql`
  mutation VerifyPrivacyCaseIdentity($input: VerifyPrivacyCaseIdentityInput!) {
    verifyPrivacyCaseIdentity(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const EXTEND_PRIVACY_CASE = gql`
  mutation ExtendPrivacyCase($input: ExtendPrivacyCaseInput!) {
    extendPrivacyCase(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const REQUEST_PRIVACY_CASE_HOLD = gql`
  mutation RequestPrivacyCaseHold($input: SetPrivacyCaseHoldInput!) {
    requestPrivacyCaseHold(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const DECIDE_PRIVACY_CASE_HOLD = gql`
  mutation DecidePrivacyCaseHold($input: DecidePrivacyCaseHoldInput!) {
    decidePrivacyCaseHold(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const LIFT_PRIVACY_CASE_HOLD = gql`
  mutation LiftPrivacyCaseHold($caseCode: String!) {
    liftPrivacyCaseHold(caseCode: $caseCode) {
      ${CASE_FIELDS}
    }
  }
`

export const ESCALATE_PRIVACY_CASE = gql`
  mutation EscalatePrivacyCase($input: EscalatePrivacyCaseInput!) {
    escalatePrivacyCase(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const RESOLVE_PRIVACY_CASE = gql`
  mutation ResolvePrivacyCase($input: ResolvePrivacyCaseInput!) {
    resolvePrivacyCase(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const DENY_PRIVACY_CASE = gql`
  mutation DenyPrivacyCase($input: DenyPrivacyCaseInput!) {
    denyPrivacyCase(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const CLOSE_PRIVACY_CASE = gql`
  mutation ClosePrivacyCase($caseCode: String!) {
    closePrivacyCase(caseCode: $caseCode) {
      ${CASE_FIELDS}
    }
  }
`

export const SET_PRIVACY_CONSERVATION_HOLD = gql`
  mutation SetPrivacyConservationHold($input: SetPrivacyConservationHoldInput!) {
    setPrivacyConservationHold(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const UPDATE_PRIVACY_PROCEDURE_STEP = gql`
  mutation UpdatePrivacyProcedureStep($input: UpdatePrivacyProcedureStepInput!) {
    updatePrivacyProcedureStep(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const ADD_PRIVACY_RESIDUAL = gql`
  mutation AddPrivacyResidual($input: AddPrivacyResidualInput!) {
    addPrivacyResidual(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const CLEAR_PRIVACY_RESIDUAL = gql`
  mutation ClearPrivacyResidual($input: ClearPrivacyResidualInput!) {
    clearPrivacyResidual(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const GENERATE_PRIVACY_ERASURE_ACTA = gql`
  mutation GeneratePrivacyErasureActa($input: GeneratePrivacyErasureActaInput!) {
    generatePrivacyErasureActa(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const UPDATE_PRIVACY_ERASURE_ACTA = gql`
  mutation UpdatePrivacyErasureActa($input: UpdatePrivacyErasureActaInput!) {
    updatePrivacyErasureActa(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const ISSUE_PRIVACY_ERASURE_ACTA = gql`
  mutation IssuePrivacyErasureActa($input: IssuePrivacyErasureActaInput!) {
    issuePrivacyErasureActa(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const CLOSE_PRIVACY_ERASURE_ACTA = gql`
  mutation ClosePrivacyErasureActa($input: ClosePrivacyErasureActaInput!) {
    closePrivacyErasureActa(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const ATTACH_PRIVACY_CASE_EVIDENCE = gql`
  mutation AttachPrivacyCaseEvidence($input: AttachPrivacyCaseEvidenceInput!) {
    attachPrivacyCaseEvidence(input: $input) {
      ${CASE_FIELDS}
    }
  }
`

export const REVEAL_PRIVACY_ERASURE_PLAYBOOK = gql`
  mutation RevealPrivacyErasurePlaybook($input: RevealPrivacyErasurePlaybookInput!) {
    revealPrivacyErasurePlaybook(input: $input) {
      stepCode
      where
      who
      env
      needsDpoPassword
      commands {
        title
        body
      }
    }
  }
`

/** A-09 — búsqueda federada del titular */
export const LAYER_LABELS = {
  SOURCE: 'Fuente',
  RECIPIENT: 'Destinatarios',
  ARCHIVED: 'Archivado',
  BACKUP: 'Respaldo',
}

export const PRIVACY_SUBJECT_SEARCH_PREPARE = gql`
  query PrivacySubjectSearchPrepare($input: PrivacySubjectSearchInput!) {
    privacySubjectSearchPrepare(input: $input) {
      searchId
      preparedAt
      liveStepCount
      totalStepCount
      subject {
        rut
        email
        userId
        name
        subjectHash
      }
      anchors {
        userIds
        companyIds
        resolvedEmail
        resolvedRut
      }
      users {
        id
        email
        name
        rut
        adminStatus
        roles
        matchReason
      }
      companies {
        id
        name
        rut
        matchReason
      }
      steps {
        sourceId
        label
        layer
        system
        collection
        externalSystem
        live
        scope
        offboardingCategory
        severity
        verify
        risk
        action
        resolvedWhen
      }
      declarativeHits {
        sourceId
        system
        label
        layer
        collection
        externalSystem
        count
        note
        skippedReason
        offboardingCategory
        verify
        risk
        action
        resolvedWhen
        severity
        samples {
          recordId
          label
          companyId
        }
      }
    }
  }
`

export const PRIVACY_SUBJECT_SEARCH_STEP = gql`
  query PrivacySubjectSearchStep($input: PrivacySubjectSearchStepInput!) {
    privacySubjectSearchStep(input: $input) {
      searchId
      completedLiveSteps
      liveStepCount
      done
      auditEventId
      hit {
        sourceId
        system
        label
        layer
        collection
        externalSystem
        count
        note
        skippedReason
        offboardingCategory
        verify
        risk
        action
        resolvedWhen
        severity
        samples {
          recordId
          label
          companyId
        }
        byCompany {
          companyId
          count
          samples {
            recordId
            label
            companyId
          }
        }
      }
    }
  }
`
